import React, { useEffect, useState, useContext, useMemo, useRef } from 'react';
import { StateContext } from '../../../StateProvider/index.js';
import {
  fetchAllUsers,
  fetchAllFamilyMember,
  uploadFileToS3,
  createIncident,
  fetchIncidents
} from '../../../Utilities/api/index.js';
import './StaffIncidentLog.css';

const MAX_DESC = 1000;
function safe(v) { return v == null ? '' : String(v); }

function normalize(str) {
  if (str == null) return '';
  return String(str).toLowerCase().trim();
}

function petAgeLabel(p) {
  var dob = p && (p.birthdate || p.dob);
  if (!dob) return 'Unknown age';
  try {
    var d = new Date(dob);
    var now = new Date();
    var yrs = now.getFullYear() - d.getFullYear();
    var m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) yrs = yrs - 1;
    if (yrs > 0) return yrs + 'y';
    var months = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
    if (now.getDate() < d.getDate()) months = months - 1;
    if (months < 1) months = 0;
    return months + 'mo';
  } catch (e) {
    return 'Unknown age';
  }
}

function petLabel(p) {
  var name = p && p.name ? p.name : 'Unnamed';
  var breed = p && p.breed ? p.breed : null;
  var age = petAgeLabel(p);
  var type = p && p.type ? p.type : '';
  var parts = [];
  parts.push(name);
  if (breed) parts.push(breed);
  if (age) parts.push(age);
  if (type) parts.push(type);
  return parts.join(' • ');
}

function ownerLabel(u) {
  var name = u && u.username ? u.username : 'Unknown';
  var email = u && u.email ? u.email : '';
  var phone = u && u.phone ? u.phone : '';
  var parts = [name];
  if (email) parts.push(email);
  if (phone) parts.push(phone);
  return parts.join(' • ');
}

function StaffIncidentLog() {
  const ctx = useContext(StateContext) || {};
  const user = ctx.user;

  // Data
  const [users, setUsers] = useState([]);                 // all owners
  const [familyMembers, setFamilyMembers] = useState([]); // all pets & humans
  const [myIncidents, setMyIncidents] = useState([]);

  // UI state
  const [loadingInit, setLoadingInit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  // Form
  const [form, setForm] = useState({
    department: 'grooming',
    incidentType: 'injury',
    severity: 'moderate',
    ownerUid: '',
    relatedPetId: '',
    description: ''
  });

  // Typeahead state
  const [ownerQuery, setOwnerQuery] = useState('');
  const [ownerSuggestions, setOwnerSuggestions] = useState([]);
  const [petQuery, setPetQuery] = useState('');
  const [petSuggestions, setPetSuggestions] = useState([]);
  const ownerListRef = useRef(null);
  const petListRef = useRef(null);
  const [ownerActiveIndex, setOwnerActiveIndex] = useState(-1);
  const [petActiveIndex, setPetActiveIndex] = useState(-1);

  // Derived: pets for selected owner (for labels and safety)
  const ownerPets = useMemo(function () {
    if (!form.ownerUid) return [];
    var out = [];
    var i = 0;
    for (i = 0; i < familyMembers.length; i++) {
      var m = familyMembers[i];
      if (!m) continue;
      if (m.userId === form.ownerUid && m.type !== 'Human') out.push(m);
    }
    return out;
  }, [form.ownerUid, familyMembers]);

  // Remaining characters
  const remaining = useMemo(function () {
    var len = form.description ? form.description.length : 0;
    var left = MAX_DESC - len;
    return left < 0 ? 0 : left;
  }, [form.description]);

  // Initial load
  useEffect(function () {
    if (!user || !user.id) return;

    async function load() {
      setLoadingInit(true);
      setErrorMsg('');
      try {
        const results = await Promise.all([
          fetchAllUsers(),
          fetchAllFamilyMember(),
          fetchIncidents()
        ]);

        const uList = Array.isArray(results[0]) ? results[0] : [];
        const fmList = Array.isArray(results[1]) ? results[1] : [];
        const incList = Array.isArray(results[2]) ? results[2] : [];

        setUsers(uList);
        setFamilyMembers(fmList);

        // Only incidents reported by this staffer
        var i = 0;
        var mine = [];
        for (i = 0; i < incList.length; i++) {
          var it = incList[i];
          if (it && it.reportedBy === user.id) mine.push(it);
        }
        // Simple sort (newest first)
        mine.sort(function (a, b) {
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
        setMyIncidents(mine);
      } catch (err) {
        setErrorMsg('Failed to load incident resources.');
      } finally {
        setLoadingInit(false);
      }
    }

    load();
  }, [user]);

  // Cleanup preview URL
  useEffect(function () {
    return function () {
      if (previewUrl) {
        try { URL.revokeObjectURL(previewUrl); } catch (e) {}
      }
    };
  }, [previewUrl]);

  // --- Typeahead filtering ---
  const filteredOwners = useMemo(function () {
    var list = [];
    var q = normalize(ownerQuery);
    var i = 0;
    if (!q) return list;

    for (i = 0; i < users.length; i++) {
      var u = users[i];
      if (!u) continue;
      var blob = normalize((u.username || '') + ' ' + (u.email || '') + ' ' + (u.phone || ''));
      if (blob.indexOf(q) !== -1) list.push(u);
      if (list.length >= 10) break;
    }
    return list;
  }, [ownerQuery, users]);

  const filteredPets = useMemo(function () {
    var list = [];
    var q = normalize(petQuery);
    var i = 0;

    var pool = [];
    if (form.ownerUid) {
      for (i = 0; i < familyMembers.length; i++) {
        var m = familyMembers[i];
        if (!m) continue;
        if (m.userId === form.ownerUid && m.type !== 'Human') pool.push(m);
      }
    } else {
      for (i = 0; i < familyMembers.length; i++) {
        var mm = familyMembers[i];
        if (!mm) continue;
        if (mm.type !== 'Human') pool.push(mm);
      }
    }

    if (!q) return [];

    for (i = 0; i < pool.length; i++) {
      var p = pool[i];
      var joined = normalize((p.name || '') + ' ' + (p.breed || '') + ' ' + (p.type || ''));
      if (joined.indexOf(q) !== -1) list.push(p);
      if (list.length >= 10) break;
    }
    return list;
  }, [petQuery, familyMembers, form.ownerUid]);

  // Apply filtered lists to visible suggestions whenever query changes
  useEffect(function () {
    setOwnerSuggestions(filteredOwners);
    setOwnerActiveIndex(-1);
  }, [filteredOwners]);

  useEffect(function () {
    setPetSuggestions(filteredPets);
    setPetActiveIndex(-1);
  }, [filteredPets]);

  // Handlers
  function onFieldChange(e) {
    var name = e && e.target ? e.target.name : '';
    var value = e && e.target ? e.target.value : '';
    setForm(function (prev) {
      var copy = {};
      for (var k in prev) copy[k] = prev[k];
      copy[name] = value;
      if (name === 'ownerUid') {
        copy.relatedPetId = '';
        // Sync query display if manual select ever used again
      }
      return copy;
    });
  }

  function onOwnerQueryChange(e) {
    var v = e && e.target ? e.target.value : '';
    setOwnerQuery(v);
  }

  function chooseOwner(u) {
    setForm(function (prev) {
      var copy = {};
      for (var k in prev) copy[k] = prev[k];
      copy.ownerUid = u.uid;
      copy.relatedPetId = '';
      return copy;
    });
    setOwnerQuery(ownerLabel(u));
    setOwnerSuggestions([]);
    setPetQuery('');
    setPetSuggestions([]);
    setOwnerActiveIndex(-1);
  }

  function onOwnerKey(e) {
    if (!ownerSuggestions || !ownerSuggestions.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      var next = ownerActiveIndex + 1;
      if (next >= ownerSuggestions.length) next = ownerSuggestions.length - 1;
      setOwnerActiveIndex(next);
      scrollIntoViewIfNeeded(ownerListRef, next);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      var prev = ownerActiveIndex - 1;
      if (prev < 0) prev = 0;
      setOwnerActiveIndex(prev);
      scrollIntoViewIfNeeded(ownerListRef, prev);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      var pick = ownerSuggestions[ownerActiveIndex >= 0 ? ownerActiveIndex : 0];
      if (pick) chooseOwner(pick);
    } else if (e.key === 'Escape') {
      setOwnerSuggestions([]);
      setOwnerActiveIndex(-1);
    }
  }

  function onPetQueryChange(e) {
    var v = e && e.target ? e.target.value : '';
    setPetQuery(v);
  }

  function choosePet(p) {
    setForm(function (prev) {
      var copy = {};
      for (var k in prev) copy[k] = prev[k];
      copy.relatedPetId = p.id;
      return copy;
    });
    setPetQuery(petLabel(p));
    setPetSuggestions([]);
    setPetActiveIndex(-1);
  }

  function onPetKey(e) {
    if (!petSuggestions || !petSuggestions.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      var next = petActiveIndex + 1;
      if (next >= petSuggestions.length) next = petSuggestions.length - 1;
      setPetActiveIndex(next);
      scrollIntoViewIfNeeded(petListRef, next);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      var prev = petActiveIndex - 1;
      if (prev < 0) prev = 0;
      setPetActiveIndex(prev);
      scrollIntoViewIfNeeded(petListRef, prev);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      var pick = petSuggestions[petActiveIndex >= 0 ? petActiveIndex : 0];
      if (pick) choosePet(pick);
    } else if (e.key === 'Escape') {
      setPetSuggestions([]);
      setPetActiveIndex(-1);
    }
  }

  function scrollIntoViewIfNeeded(listRef, index) {
    try {
      var el = listRef && listRef.current ? listRef.current.children[index] : null;
      if (!el) return;
      var parent = listRef.current;
      var elTop = el.offsetTop;
      var elBottom = elTop + el.offsetHeight;
      var viewTop = parent.scrollTop;
      var viewBottom = viewTop + parent.clientHeight;
      if (elTop < viewTop) parent.scrollTop = elTop;
      else if (elBottom > viewBottom) parent.scrollTop = elBottom - parent.clientHeight;
    } catch (e) {}
  }

  function onFileChange(e) {
    var f = (e && e.target && e.target.files && e.target.files[0]) ? e.target.files[0] : null;

    if (previewUrl) {
      try { URL.revokeObjectURL(previewUrl); } catch (err) {}
    }

    setFile(f);
    try {
      setPreviewUrl(f ? URL.createObjectURL(f) : '');
    } catch (err) {
      setPreviewUrl('');
    }
  }

  function resetForm() {
    setForm({
      department: 'grooming',
      incidentType: 'injury',
      severity: 'moderate',
      ownerUid: '',
      relatedPetId: '',
      description: ''
    });
    setFile(null);
    if (previewUrl) {
      try { URL.revokeObjectURL(previewUrl); } catch (e) {}
    }
    setPreviewUrl('');
    setOwnerQuery('');
    setOwnerSuggestions([]);
    setPetQuery('');
    setPetSuggestions([]);
  }

  async function handleSubmit(e) {
    if (e && e.preventDefault) e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');

    if (!user || !user.id) {
      setErrorMsg('Unable to identify current staff. Please re-login.');
      return;
    }

    var desc = safe(form.description).trim();
    if (desc.length < 5) {
      setErrorMsg('Please provide a brief description (at least 5 characters).');
      return;
    }
    if (desc.length > MAX_DESC) {
      setErrorMsg('Description is too long (max ' + MAX_DESC + ' characters).');
      return;
    }

    setSubmitting(true);
    try {
      var uploadedUrl = null;
      if (file) {
        uploadedUrl = await uploadFileToS3(file);
      }

      var i = 0;
      var owner = null;
      for (i = 0; i < users.length; i++) {
        var u = users[i];
        if (u && u.uid === form.ownerUid) { owner = u; break; }
      }

      var payload = {
        reportedBy: user.id,
        relatedPetId: form.relatedPetId ? Number(form.relatedPetId) : null,
        ownerUid: form.ownerUid || null,
        ownerName: owner ? owner.username : null,
        department: form.department,
        incidentType: form.incidentType,
        severity: form.severity,
        description: desc,
        status: 'open',
        attachments: uploadedUrl ? [{ url: uploadedUrl, kind: 'photo' }] : []
      };

      await createIncident(payload);

      setSuccessMsg('Incident submitted.');
      resetForm();

      // Refresh my incidents
      try {
        var all = await fetchIncidents();
        var mine = [];
        for (i = 0; i < all.length; i++) {
          var it = all[i];
          if (it && it.reportedBy === user.id) mine.push(it);
        }
        mine.sort(function (a, b) {
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
        setMyIncidents(mine);
      } catch (err2) {}
    } catch (err) {
      setErrorMsg('Failed to submit incident. Try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="sil-wrap">
      <div className="sil-header">
        <h2 className="sil-title">🐾 Incident Report</h2>
        <p className="sil-sub">Link to an owner & pet when relevant. Keep it factual and concise.</p>
      </div>

      {errorMsg ? <div className="sil-banner sil-banner--error" role="alert">{errorMsg}</div> : null}
      {successMsg ? <div className="sil-banner sil-banner--success">{successMsg}</div> : null}

      <form className="sil-form" onSubmit={handleSubmit} noValidate>
        <div className="sil-grid">
          <div className="sil-field">
            <label className="sil-label" htmlFor="department">Department</label>
            <select id="department" name="department" className="sil-input" value={form.department} onChange={onFieldChange}>
              <option value="grooming">Grooming</option>
              <option value="daycare">Daycare</option>
              <option value="frontdesk">Front Desk</option>
              <option value="training">Training</option>
              <option value="hotel">Hotel</option>
              <option value="shuttle">Shuttle</option>
            </select>
          </div>

          <div className="sil-field">
            <label className="sil-label" htmlFor="incidentType">Incident Type</label>
            <select id="incidentType" name="incidentType" className="sil-input" value={form.incidentType} onChange={onFieldChange}>
              <option value="injury">Injury</option>
              <option value="behavior">Behavior</option>
              <option value="equipment">Equipment</option>
              <option value="facility">Facility</option>
              <option value="customer">Customer</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="sil-field">
            <label className="sil-label" htmlFor="severity">Severity</label>
            <select id="severity" name="severity" className="sil-input" value={form.severity} onChange={onFieldChange}>
              <option value="low">Low</option>
              <option value="moderate">Moderate</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>

          {/* Owner typeahead */}
          <div className="sil-field">
            <label className="sil-label" htmlFor="ownerSearch">Owner (optional)</label>
            <input
              id="ownerSearch"
              type="text"
              className="sil-input"
              placeholder="Type name, email, or phone"
              value={ownerQuery}
              onChange={onOwnerQueryChange}
              onKeyDown={onOwnerKey}
              autoComplete="off"
              aria-autocomplete="list"
              aria-controls="owner-suggest"
              aria-expanded={ownerSuggestions && ownerSuggestions.length ? true : false}
            />
            {ownerSuggestions && ownerSuggestions.length ? (
              <ul
                id="owner-suggest"
                className="sil-suggest"
                ref={ownerListRef}
                role="listbox"
              >
                {ownerSuggestions.map(function (u, idx) {
                  return (
                    <li key={u.uid} role="option" aria-selected={idx === ownerActiveIndex}>
                      <button
                        type="button"
                        className={'sil-suggest-item' + (idx === ownerActiveIndex ? ' is-active' : '')}
                        onMouseDown={function (ev) { ev.preventDefault(); }}
                        onClick={function () { chooseOwner(u); }}
                      >
                        {ownerLabel(u)}
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
            {form.ownerUid ? (
              <div className="sil-help">Selected owner: {ownerLabel(findOwnerByUid(users, form.ownerUid))}</div>
            ) : <div className="sil-help">No owner selected</div>}
          </div>

          {/* Pet typeahead */}
          <div className="sil-field">
            <label className="sil-label" htmlFor="petSearch">Related Pet (optional)</label>
            <input
              id="petSearch"
              type="text"
              className="sil-input"
              placeholder={form.ownerUid ? 'Type pet name, breed, or type' : 'Type pet name or breed (all clients)'}
              value={petQuery}
              onChange={onPetQueryChange}
              onKeyDown={onPetKey}
              autoComplete="off"
              aria-autocomplete="list"
              aria-controls="pet-suggest"
              aria-expanded={petSuggestions && petSuggestions.length ? true : false}
              disabled={familyMembers.length === 0}
            />
            {petSuggestions && petSuggestions.length ? (
              <ul
                id="pet-suggest"
                className="sil-suggest"
                ref={petListRef}
                role="listbox"
              >
                {petSuggestions.map(function (p, idx) {
                  return (
                    <li key={p.id} role="option" aria-selected={idx === petActiveIndex}>
                      <button
                        type="button"
                        className={'sil-suggest-item' + (idx === petActiveIndex ? ' is-active' : '')}
                        onMouseDown={function (ev) { ev.preventDefault(); }}
                        onClick={function () { choosePet(p); }}
                      >
                        {petLabel(p)}
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
            {form.relatedPetId ? (
              <div className="sil-help">
                Selected pet: {petLabel(findPetById(ownerPets, form.relatedPetId))}
              </div>
            ) : <div className="sil-help">No pet selected</div>}
          </div>
        </div>

        <div className="sil-field">
          <label className="sil-label" htmlFor="description">What happened?</label>
          <textarea
            id="description"
            name="description"
            className="sil-textarea"
            rows={6}
            value={form.description}
            onChange={onFieldChange}
            maxLength={MAX_DESC}
            placeholder="Who / what / when / where. Include steps taken and follow-ups."
            aria-describedby="desc-help"
            required
          />
          <div id="desc-help" className="sil-help">{remaining} characters left</div>
        </div>

        <div className="sil-field">
          <label className="sil-label" htmlFor="attachment">Photo (optional)</label>
          <input id="attachment" type="file" accept="image/*" onChange={onFileChange} className="sil-file" />
          {previewUrl ? (
            <div className="sil-preview">
              <img src={previewUrl} alt="Attachment preview" />
            </div>
          ) : null}
        </div>

        <div className="sil-actions">
          <button type="submit" className="sil-btn sil-btn--primary" disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit Incident'}
          </button>
        </div>
      </form>

      <hr className="sil-divider" />

      <h3 className="sil-subtitle">My Recent Incidents</h3>

      {loadingInit && !myIncidents.length ? <div className="sil-muted">Loading…</div> : null}
      {!loadingInit && myIncidents.length === 0 ? <div className="sil-muted">No incidents filed yet.</div> : null}

      <div className="sil-list">
        {myIncidents.map(function (i) {
          var sev = i && i.severity ? String(i.severity).toLowerCase() : 'low';
          var created = i && i.createdAt ? new Date(i.createdAt).toLocaleString() : '-';
          return (
            <article key={i.id} className="sil-card">
              <header className="sil-card-head">
                <span className={'sil-chip sil-chip--' + sev}>
                  {(i && i.severity ? String(i.severity).toUpperCase() : 'LOW')}
                </span>
                <div className="sil-meta">
                  <span className="sil-meta-item">{i && i.department}</span>
                  <span className="sil-dot">•</span>
                  <span className="sil-meta-item">{i && i.incidentType}</span>
                  <span className="sil-dot">•</span>
                  <time className="sil-meta-item">{created}</time>
                </div>
              </header>

              <p className="sil-desc">{i && i.description}</p>

              {i && i.attachments && i.attachments.length ? (
                <div className="sil-attachments">
                  {i.attachments.map(function (a, idx) {
                    return (
                      <a key={idx} className="sil-attachment" href={a.url} target="_blank" rel="noreferrer">
                        Attachment {idx + 1}
                      </a>
                    );
                  })}
                </div>
              ) : null}

              <footer className="sil-status">
                Status: <strong>{i && i.status}</strong>
              </footer>
            </article>
          );
        })}
      </div>
    </div>
  );
}

// Small helpers without chaining
function findOwnerByUid(users, uid) {
  var i = 0;
  for (i = 0; i < users.length; i++) {
    var u = users[i];
    if (u && u.uid === uid) return u;
  }
  return {};
}

function findPetById(pets, id) {
  var i = 0;
  for (i = 0; i < pets.length; i++) {
    var p = pets[i];
    if (p && String(p.id) === String(id)) return p;
  }
  return {};
}

export default StaffIncidentLog;
